import React, {
  useMemo,
  useState,
  useEffect,
} from 'react';
import MainAppLayout from 'components/templates/MainAppLayout/MainAppLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteKeys } from 'models/RouteModels';
import { usePatientData } from 'hooks/usePatientData';
import { DataCardHeader } from 'components/styled/StyledDashboardComponents';
import DateDisplay from 'components/atoms/DateDisplay/DateDisplay';
import { Breakpoints, MUIColors } from 'models/StyleModels';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faCircleNotch, faCirclePlus, faEye, faPen, faUser } from '@fortawesome/free-solid-svg-icons';
import colors from 'assets/theme/base/colors';
import { getAgeFromDOBString } from 'helpers/helpers';
import { useAppointmentData } from 'hooks/useAppointmentData';
import MDBox from 'components/atoms/MDBox/MDBox';

import { ReasonsForVisit, TriageXrayStatus } from 'models/AppointmentModels';
import StatusCell from 'components/pages/TriageListPage/StatusCell';
import SelectedJointsCell from 'components/pages/TriageListPage/SelectedJointsCell';
import { useNavigation } from 'hooks/useNavigation';
import { useProData } from 'hooks/useProData';
import { useXrayData } from 'hooks/useXrayData';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { loadingAnimation } from 'assets/animations/animationStyles';
import { useAppointmentMutations } from 'hooks/useAppointmentMutations';
import { getCurrentISOTimestamp } from 'services/dateService';
import { useAuth } from 'hooks/useAuth';
import { SurveyTypes } from 'models/SurveyModels';
import Alert from 'components/atoms/Alert/Alert';
import ActionSlugs, { IfUserIsAllowed } from 'services/PermissionsService';
import EditPatientOverviewModal from 'components/organisms/EditPatientOverviewModal/EditPatientOverviewModal';
import TriageDataTable from 'components/organisms/TriageDataTable';
import { TableColumnType } from 'components/organisms/DataTable';
import MDButton from 'components/atoms/MDButton/MDButton';
import { routesMap } from 'data/routes';
import {
  PatientOverviewContent,
  PatientOverviewGrid,
  BackButton,
  NavigationContainer,
  ChevronIcon,
  PatientNameButton,
  PatientOverviewCardContent,
  PatientHeaderGrid,
  PatientNameHeader,
  LoadingHeaderContainer,
  LoadingDetailsContainer,
  LoadingSkeletonBox,
  PatientDetailsContainer,
  PatientDetailRow,
  PatientDetailText,
  PatientDetailLabelBold,
  PatientDetailLabelMedium,
  HeaderButtonsContainer,
  EditInfoButton,
  NewExamButton,
  ExamHistoryContainer,
  ExamHistoryHeader,
  ExamRoomIcon,
  DataTableItemText,
  PatientOverviewCard
} from './PatientOverviewPage.styles';

// TODO: this page should probably allow users with permission to edit patient information
// to be able to do so here on the patient details card

const PatientOverviewPage: React.FC = () => {
  const { patientId } = useParams();
  const { navigateToBaseRouteWithAptmtDetails } = useNavigation();
  const { breakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });
  const { user } = useAuth();
  const { CREATE$APPOINTMENT } = ActionSlugs;

  const { breakpointBreached: lgBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });

  const navigate = useNavigate();

  const {
    singlePatient,
    singlePatientLoading,
  } = usePatientData({ patientId });

  const {
    allAppointmentsByPatient,
    allAppointmentsByPatientLoading,
  } = useAppointmentData({ patientId });

  const {
    createAppointment,
    createAppointmentError,
    createAppointmentLoading,
  } = useAppointmentMutations();

  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState<boolean>(false);

  useEffect(() => {
    if (!createAppointmentError) return;
    setShowErrorAlert(true);
  }, [createAppointmentError])

  const {
    allProsByPatientLoading,
  } = useProData({ patientId });

  const {
    allXraysByPatientLoading,
  } = useXrayData({ patientId });

  const isLoading = singlePatientLoading
    || allAppointmentsByPatientLoading
    || allProsByPatientLoading
    || allXraysByPatientLoading;

  const tableData = useMemo(() => allAppointmentsByPatient?.sort((a, b) => (new Date(b.appointment_date) as any) - (new Date(a.appointment_date) as any))
    .map((appointment) => ({
      json: appointment,

      appointment_date: appointment.appointment_date,
      xray_status: appointment.xray_status,
      pro_status: appointment.pro_status,
      provider: appointment.physician,
      appointment_id: appointment.appointment_id
    })
    ), [allAppointmentsByPatient]);

  const tableColumns: TableColumnType[] = [
    {
      Header: 'Date of Appt.',
      accessor: 'created_at',
      width: '5%',
      align: 'left',
      canSort: true,
      Cell: ({ row }: any) => {
        const appt = row.original.json;
        const date = appt?.appointment_date;
        return date ? <DateDisplay isoString={date} showTime={false} /> : <span>-</span>;
      }
    },
    {
      Header: 'PROs',
      accessor: 'pros',
      width: '10%',
      align: 'center',
      canSort: true,
      Cell: ({ row }: any) => (
        <StatusCell
          appointment={row.original.json}
          kind='PROS'
          lgBreakpointBreached={breakpointBreached}
          navigateToBaseRouteWithAptmtDetails={navigateToBaseRouteWithAptmtDetails}
        />
      )
    },
    {
      Header: 'X-rays',
      accessor: 'xray',
      width: '10%',
      align: 'center',
      canSort: true,
      Cell: ({ row }: any) => (
        <StatusCell
          appointment={row.original.json}
          kind='XRAYS'
          lgBreakpointBreached={breakpointBreached}
          navigateToBaseRouteWithAptmtDetails={navigateToBaseRouteWithAptmtDetails}
        />
      )
    },
    {
      Header: 'Selected Joints',
      accessor: 'selected_joints',
      width: '8%',
      align: 'left',
      canSort: true,
      Cell: ({ row }: any) => (
        <SelectedJointsCell appointment={row.original.json} />
      )
    },
    {
      Header: 'Provider',
      accessor: 'provider',
      width: '10%',
      align: 'left',
      canSort: true,
      Cell: ({ row }: any) => (
        <DataTableItemText>
          {singlePatient?.provider || 'N/A'}
        </DataTableItemText>
      )
    },
    {
      Header: '',
      accessor: '',
      width: '10%',
      align: 'left',
      canSort: true,
      Cell: ({ row }: any) => (
        ExamRoomButton(row)
      )
    },
  ]

  // the New Exam action button on this page acts very similarly to the Quickstart Exam function, creating a
  // new appointment for the patient you're viewing and filling in default values for all of the fields in that exam
  // with that moment's timestamp as the appointment_date. it then navigates the user to that exam's Appointment Overview
  // page
  const handleCreateAppointment = async () => {
    if (!singlePatient || !user || isLoading) return;

    const newExamData = await createAppointment({
      patient_id: singlePatient.patient_id,
      appointment_date: getCurrentISOTimestamp(),
      org_id: user.orgs[0].org_id,
      survey_type: SurveyTypes.HOOS_KOOS,
      reason_for_visit: ReasonsForVisit.EVALUATION,
    }).catch(() => setShowErrorAlert(true));

    const newExam = newExamData?.data.appointment;

    if (!newExam) return;

    navigateToBaseRouteWithAptmtDetails({
      routeKey: RouteKeys.APPOINTMENT_OVERVIEW,
      appointmentDateISOString: newExam.appointment_date,
      appointmentId: newExam.appointment_id,
    });
  }

  const ExamRoomButton = (row: any) => {
    const appointment = row.original.json; // Access the appointment object directly
    if (!appointment) return null;

    if (
      [TriageXrayStatus.NO_XRAYS, TriageXrayStatus.XRAYS_MISSING].includes(appointment.xray_status)) {

      return (
        <div />
      )
    }

    if (appointment?.xray_status === TriageXrayStatus.READY_FOR_REVIEW || appointment?.xray_status === TriageXrayStatus.COMPLETED) {

      return (
        <MDButton
          variant='contained'
          size={lgBreakpointBreached ? 'small' : 'medium'}
          onClick={() => {
            navigateToBaseRouteWithAptmtDetails({
              routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
              appointmentId: appointment.appointment_id,
              appointmentDateISOString: appointment.appointment_date,
              addtlQueryParams: { flow: 'TRIAGE' }
            })
          }}
          color={MUIColors.SECONDARY}
        >
          <ExamRoomIcon color={colors.white.focus} icon={faEye} />
          Exam Room
        </MDButton>
      )
    }

    return null
  }

  return (
    <>
      {showEditPatientModal && (<EditPatientOverviewModal
        onSuccess={() => {
          setShowEditPatientModal(false)
        }}
        patient={singlePatient}
        closeModal={() => setShowEditPatientModal(false)} />)}

      <Alert
        type='error'
        show={showErrorAlert}
        handleClose={() => setShowErrorAlert(false)}
      >
        Something went wrong. Please try again later.
      </Alert>
      <MainAppLayout
        headerItems={{
          headline: singlePatient ? `${singlePatient.first_name} ${singlePatient.last_name}` : '-',
          subItems: [{ 'Date of Birth': singlePatient?.date_of_birth ? <DateDisplay isoString={singlePatient.date_of_birth} showTime={false} /> : '-' }],
          pageTitle: "Patient Overview"
        }}
        navActions={{
          next: { hide: true },
        }}
      >
        {/* <PatientOverviewContainer> */}
        <PatientOverviewContent breakpointBreached={breakpointBreached}>
          <PatientOverviewGrid breakpointBreached={breakpointBreached}>
            <NavigationContainer>
              <BackButton
                onClick={() => navigate(routesMap[RouteKeys.TRIAGE_LIST].route)}
              >
                Patient List
              </BackButton>
              <ChevronIcon
                icon={faChevronRight}
                color={colors.secondary.main}
              />
              <PatientNameButton>
                {singlePatient ? `${singlePatient.first_name} ${singlePatient.last_name}` : '-'}
              </PatientNameButton>
            </NavigationContainer>
            <PatientOverviewCard breakpointBreached={breakpointBreached}>
              <PatientOverviewCardContent breakpointBreached={breakpointBreached}>
                <div>
                  {!isLoading && (
                    <PatientHeaderGrid>
                      <FontAwesomeIcon color={colors.secondary.main} icon={faUser} size="2x" />
                      <PatientNameHeader>
                        {singlePatient?.first_name} {singlePatient?.last_name}
                      </PatientNameHeader>
                    </PatientHeaderGrid>
                  )}
                  {isLoading && (
                    <LoadingHeaderContainer>
                      <MDBox
                        sx={{
                          width: '90%',
                          height: '2.35rem',
                          borderRadius: '4px',
                          ...loadingAnimation('backgroundColor'),
                        }}
                      />
                      <MDBox
                        sx={{
                          width: '100%',
                          height: '1rem',
                          borderRadius: '4px',
                          ...loadingAnimation('backgroundColor'),
                        }}
                      />
                    </LoadingHeaderContainer>
                  )}

                  {!isLoading && (
                    <PatientDetailsContainer>
                      <PatientDetailRow flexCenter={true}>
                        <PatientDetailText leftAligned={true}>
                          <PatientDetailLabelBold>Age: </PatientDetailLabelBold>{singlePatient?.date_of_birth ? getAgeFromDOBString(singlePatient.date_of_birth) : '-'}
                        </PatientDetailText>
                        <PatientDetailText>
                          <PatientDetailLabelBold>ID Number: </PatientDetailLabelBold>{singlePatient?.external_id || 'N/A'}
                        </PatientDetailText>
                      </PatientDetailRow>
                      <PatientDetailRow flexCenter={true}>
                        <PatientDetailText leftAligned={true}>
                          <PatientDetailLabelMedium>Date of Birth: </PatientDetailLabelMedium>{singlePatient?.date_of_birth || 'N/A'}
                        </PatientDetailText>
                      </PatientDetailRow>
                    </PatientDetailsContainer>
                  )}
                </div>
                {isLoading && (
                  <>
                    {Array.from(new Array(2), (e, i) => (
                      <LoadingDetailsContainer key={`container-${i}`}>
                        {Array.from(new Array(2), (e2, i2) => (
                          <LoadingSkeletonBox
                            key={`child-${i}-${i2}`}
                            sx={{
                              ...loadingAnimation('backgroundColor'),
                            }}
                          />
                        ))}
                      </LoadingDetailsContainer>
                    ))}
                  </>
                )}
                {!isLoading && (
                  <HeaderButtonsContainer>
                    <EditInfoButton
                      startIcon={<FontAwesomeIcon icon={faPen} color={colors.secondary.main} />}
                      variant='outlined'
                      color={MUIColors.SECONDARY}
                      disabled={createAppointmentLoading}
                      onClick={() => setShowEditPatientModal(true)}>
                      Edit
                    </EditInfoButton>
                    <IfUserIsAllowed to={CREATE$APPOINTMENT}>
                      <NewExamButton
                        variant='contained'
                        color={MUIColors.SECONDARY}
                        disabled={createAppointmentLoading}
                        onClick={handleCreateAppointment}>
                        {!createAppointmentLoading && (
                          <>
                            <FontAwesomeIcon icon={faCirclePlus} color={colors.white.main} fontWeight={600} />
                            New Evaluation
                          </>
                        )}
                        {createAppointmentLoading && <FontAwesomeIcon icon={faCircleNotch} color={colors.white.main} spin />}
                      </NewExamButton>
                    </IfUserIsAllowed>
                  </HeaderButtonsContainer>
                )}
              </PatientOverviewCardContent>
            </PatientOverviewCard>
          </PatientOverviewGrid>
        </PatientOverviewContent>
        <ExamHistoryContainer breakpointBreached={breakpointBreached}>
          <ExamHistoryHeader>
            <DataCardHeader>
              Exam History
            </DataCardHeader>
          </ExamHistoryHeader>
          <TriageDataTable table={{
            columns: tableColumns,
            rows: tableData
          }} />
        </ExamHistoryContainer>
        {/* </PatientOverviewContainer> */}
      </MainAppLayout >
    </>
  );
};

export default PatientOverviewPage;
